import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { m } from "framer-motion";
import { useState, useMemo } from "react";
import heroDashboard from "@/assets/hero-dashboard.jpg";
import { WaitlistForm } from "./WaitlistForm";
import { SocialProofStats } from "./SocialProofStats";
import { AuthDialog } from "./AuthDialog";

// Memoize animation variants to prevent recalculation
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const fadeInScale = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
};

export const Hero = () => {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authView, setAuthView] = useState<"login" | "signup">("signup");
  
  const openAuthDialog = (view: "login" | "signup") => {
    setAuthView(view);
    setAuthDialogOpen(true);
  };

  return <>
      <section className="relative flex items-center justify-center py-12 md:py-16 pt-20 overflow-hidden" style={{ contain: "layout style" }}>
        {/* Animated gradient mesh background */}
        <div className="absolute inset-0 gradient-mesh-bg opacity-50 pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <div className="flex flex-col items-center text-center gap-0 py-0">
            {/* Floating Badges - use CSS animation instead of JS */}
            <m.div 
              {...fadeInUp}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="flex flex-wrap gap-3 justify-center mb-6"
              style={{ willChange: "transform, opacity" }}
            >
              <Badge variant="secondary" className="glass-card px-4 py-2 animate-float">
                <Check className="mr-1 h-3 w-3" />
                Free Forever
              </Badge>
              <Badge variant="secondary" className="glass-card px-4 py-2 animate-float" style={{ animationDelay: "0.2s" }}>
                <Check className="mr-1 h-3 w-3" />
                No Credit Card
              </Badge>
              <Badge variant="secondary" className="glass-card px-4 py-2 animate-float" style={{ animationDelay: "0.4s" }}>
                <Check className="mr-1 h-3 w-3" />
                500+ Designers
              </Badge>
            </m.div>

            {/* Headline */}
            <m.h1 
              {...fadeInUp}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold max-w-5xl leading-tight"
              style={{ willChange: "transform, opacity" }}
            >
              Transform Figma Design Files Into{" "}
              <span className="gradient-text">Developer Documentation</span>
            </m.h1>

            {/* Subheadline */}
            <m.p 
              {...fadeInUp}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-xl md:text-2xl text-muted-foreground max-w-3xl mt-4 mb-6"
              style={{ willChange: "transform, opacity" }}
            >
              Convert Figma files to dev-ready documentation in 30 seconds. AI-powered design-to-dev transformation that saves 20+ hours per week.
            </m.p>

            {/* Auth Buttons */}
            <m.div 
              {...fadeInUp}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-wrap gap-4 justify-center"
              style={{ willChange: "transform, opacity" }}
            >
              <Button onClick={() => openAuthDialog("signup")} size="lg" className="gradient-button text-lg px-8">
                Get Started Free
              </Button>
              <Button onClick={() => openAuthDialog("login")} size="lg" variant="outline" className="glass-card text-lg px-8">
                Sign In
              </Button>
            </m.div>

            {/* Stats */}
            <m.div 
              {...fadeInUp}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="w-full mt-6"
              style={{ willChange: "transform, opacity" }}
            >
              <SocialProofStats />
            </m.div>

            {/* Hero Image - Optimized for LCP */}
            <m.div 
              {...fadeInScale}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mt-12 w-full max-w-6xl"
              style={{ willChange: "transform, opacity" }}
            >
              
            </m.div>
          </div>
        </div>
      </section>

      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} defaultView={authView} />
    </>;
};